/**
 * Opening/closing motion for the product page's accordion rows (`blocks/_pdp-accordion.liquid`).
 *
 * The rows are native <details>, which snap open. CSS alone (::details-content with
 * `interpolate-size`) only animates the height in Chromium, and most visitors are on iPhones, so
 * the height is animated here with the Web Animations API instead. Without JS, or with reduced
 * motion, the rows keep working as plain <details>.
 */

const SELECTOR = '.pdp-accordion';
const DURATION = 280;
const EASING = 'cubic-bezier(0.2, 0.7, 0.3, 1)';

/** @type {WeakMap<HTMLDetailsElement, Animation>} */
const running = new WeakMap();

/**
 * @param {HTMLDetailsElement} details
 * @param {boolean} opening
 */
function animate(details, opening) {
  const content = /** @type {HTMLElement | null} */ (details.querySelector(':scope > .pdp-accordion__content'));
  if (!content) {
    details.open = opening;
    return;
  }

  // Start from wherever an interrupted animation left the row
  const current = running.get(details);
  const startHeight = current ? content.getBoundingClientRect().height : opening ? 0 : content.scrollHeight;
  current?.cancel();

  details.classList.toggle('is-closing', !opening);
  if (opening) details.open = true;

  const endHeight = opening ? content.scrollHeight : 0;
  const animation = content.animate(
    [
      { height: `${startHeight}px`, opacity: opening ? 0 : 1 },
      { height: `${endHeight}px`, opacity: opening ? 1 : 0 },
    ],
    { duration: DURATION, easing: EASING }
  );
  running.set(details, animation);

  animation.onfinish = () => {
    running.delete(details);
    details.classList.remove('is-closing');
    if (!opening) details.open = false;
  };
  animation.oncancel = () => running.delete(details);
}

document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const summary = target?.closest(`${SELECTOR} > summary`);
  if (!summary) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const details = /** @type {HTMLDetailsElement} */ (summary.parentElement);
  event.preventDefault();
  // A row that is closing still has `open`; a click then reopens it
  const opening = !details.open || details.classList.contains('is-closing');
  animate(details, opening);
});
